// src/pages/DashboardPage.tsx
import * as React from "react";

import Grid from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";

import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import RefreshIcon from "@mui/icons-material/Refresh";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import MonetizationOnOutlinedIcon from "@mui/icons-material/MonetizationOnOutlined";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import RateReviewOutlinedIcon from "@mui/icons-material/RateReviewOutlined";

type MiniStat = { label: string; value: number | string; accent?: boolean };
type ListItem = { title: string; meta?: string; sub?: string };

const CardShell = ({
  title,
  icon,
  right,
  children,
  height,
}: {
  title: string;
  icon?: React.ReactNode;
  right?: React.ReactNode;
  children: React.ReactNode;
  height?: number;
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
        bgcolor: "background.paper",
        height: height ?? 260,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 2, py: 1.5 }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          {icon ? (
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: "50%",
                display: "grid",
                placeItems: "center",
                bgcolor: "action.hover",
              }}
            >
              {icon}
            </Box>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={0.7}>
            <Typography sx={{ fontWeight: 900 }}>{title}</Typography>
          </Stack>
        </Stack>

        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton size="small">
            <RefreshIcon fontSize="small" />
          </IconButton>
          {right ?? (
            <IconButton size="small">
              <MoreVertIcon fontSize="small" />
            </IconButton>
          )}
        </Stack>
      </Stack>

      <Divider />

      <Box sx={{ p: 2 }}>{children}</Box>
    </Paper>
  );
};

const MiniStatsRow = ({ items }: { items: MiniStat[] }) => {
  return (
    <Stack
      direction="row"
      divider={<Divider orientation="vertical" flexItem />}
      sx={{
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
    >
      {items.map((it, idx) => (
        <Box
          key={`${it.label}-${idx}`}
          sx={{
            flex: 1,
            px: 2,
            py: 1.5,
            minWidth: 0,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{it.label}</Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontWeight: 900,
              fontSize: 20,
              color: it.accent ? "primary.main" : "text.primary",
            }}
          >
            {it.value}
          </Typography>
        </Box>
      ))}
    </Stack>
  );
};

const StatSteps = ({
  items,
}: {
  items: { label: string; value: number; clickable?: boolean }[];
}) => {
  return (
    <Stack
      direction="row"
      sx={{
        borderRadius: 1.5,
        border: "1px solid",
        borderColor: "divider",
        overflow: "hidden",
      }}
      divider={<Divider orientation="vertical" flexItem />}
    >
      {items.map((it, idx) => (
        <Box
          key={`${it.label}-${idx}`}
          sx={{
            flex: 1,
            px: 2,
            py: 1.5,
            minWidth: 0,
            cursor: it.clickable ? "pointer" : "default",
            "&:hover": it.clickable ? { bgcolor: "action.hover" } : undefined,
          }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{it.label}</Typography>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mt: 0.5 }}
          >
            <Typography sx={{ fontWeight: 900, fontSize: 22 }}>{it.value}</Typography>
            {it.clickable ? (
              <ChevronRightIcon sx={{ color: "text.disabled" }} fontSize="small" />
            ) : null}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
};

const SimpleList = ({ items }: { items: ListItem[] }) => {
  return (
    <Stack sx={{ borderRadius: 1.5, border: "1px solid", borderColor: "divider" }}>
      {items.map((it, idx) => (
        <Box key={`${it.title}-${idx}`} sx={{ px: 2, py: 1.4 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack sx={{ minWidth: 0 }}>
              <Typography sx={{ fontWeight: 700 }} noWrap>
                {it.title}
              </Typography>
              {it.sub ? (
                <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                  {it.sub}
                </Typography>
              ) : null}
            </Stack>

            {it.meta ? (
              <Typography sx={{ fontSize: 12, color: "text.secondary", ml: 2 }}>
                {it.meta}
              </Typography>
            ) : null}
          </Stack>

          {idx !== items.length - 1 ? <Divider sx={{ mt: 1.2 }} /> : null}
        </Box>
      ))}
    </Stack>
  );
};

const PlaceholderChart = () => {
  return (
    <Box
      sx={{
        height: 160,
        borderRadius: 1.5,
        border: "1px dashed",
        borderColor: "divider",
        bgcolor: "action.hover",
        display: "grid",
        placeItems: "center",
        color: "text.disabled",
      }}
    >
      <Typography sx={{ fontSize: 13 }}>차트 영역(placeholder)</Typography>
    </Box>
  );
};

export default function DashboardPage() {
  // ✅ 더미 데이터 (너가 나중에 API 연결해서 바꾸면 됨)
  const salesSteps = [
    { label: "신규주문", value: 4, clickable: true },
    { label: "배송준비", value: 247, clickable: true },
    { label: "배송중", value: 801, clickable: true },
    { label: "배송완료", value: 1864, clickable: true },
    { label: "구매확정", value: 2964, clickable: true },
  ];

  const settlementStats: MiniStat[] = [
    { label: "오늘정산", value: "24,255,715원", accent: true },
    { label: "정산예정", value: "27,875,125원" },
    { label: "비즈월렛", value: "1,440,651원" },
  ];

  const cancelStats = [
    { label: "취소요청", value: 0 },
    { label: "반품요청", value: 9 },
    { label: "반품수거완료", value: 10 },
    { label: "교환요청", value: 2 },
    { label: "교환수거완료", value: 8 },
  ];

  const delayStats = [
    { label: "발송지연", value: 0 },
    { label: "취소지연", value: 0 },
    { label: "반품지연", value: 3 },
    { label: "교환지연", value: 7 },
  ];

  const productStats = [
    { label: "판매중 상품", value: 166 },
    { label: "품절 상품", value: 42 },
    { label: "수정요청 상품", value: 0 },
    { label: "그룹상품 전환 가능", value: 68 },
  ];

  const reviewStats = [
    { label: "새로 작성된 리뷰", value: 1479, accent: true },
    { label: "낮은 평점 리뷰", value: 9 },
    { label: "발표 예정 리뷰 이벤트", value: 0 },
  ];

  const inquiryTabs = [
    { label: "톡톡 문의", value: 12, accent: true },
    { label: "주문 고객 문의", value: 3 },
    { label: "상품 Q&A", value: 9 },
  ];

  const inquiryList: ListItem[] = [
    { title: "혹시 몰라서 동영상도 찍어놨습니다", sub: "세리 | 2026.01.01 20:42" },
    { title: "사진보낸거 두개다 토요일까지 빠르게 배송해주세요", sub: "alw | 2026.01.01 16:31" },
    {
      title: "왜 제가 확인이 늦었습니다. 김소영/01066637028/대구시 북구 ...",
      sub: "사랑동 | 2026.01.01 16:07",
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Grid container spacing={2}>
        {/* 상단: 판매관리(좌) + 정산관리(우) */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <CardShell
            height={250}
            title="판매 관리"
            icon={<ShoppingBagOutlinedIcon fontSize="small" />}
          >
            <StatSteps items={salesSteps} />

            {/* <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
              <MiniStatsRow
                items={[
                  { label: "선물수락대기", value: 1 },
                  { label: "오늘출발", value: 0 },
                  { label: "예약구매", value: 0 },
                  { label: "정기구독", value: 0 },
                  { label: "도착보장", value: 1 },
                ]}
              />
            </Stack> */}
          </CardShell>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <CardShell
            height={250}
            title="정산 관리"
            icon={<MonetizationOnOutlinedIcon fontSize="small" />}
          >
            <Stack spacing={1.2}>
              <Box
                sx={{
                  borderRadius: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  p: 2,
                }}
              >
                <Stack spacing={1.2}>
                  {settlementStats.map((it) => (
                    <Stack
                      key={it.label}
                      direction="row"
                      alignItems="baseline"
                      justifyContent="space-between"
                      spacing={2}
                    >
                      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                        {it.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: it.accent ? 24 : 16,
                          color: it.accent ? "primary.main" : "text.primary",
                        }}
                      >
                        {it.value}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>

              {/* <Button variant="outlined" fullWidth>
                정산 상세 보기
              </Button> */}
            </Stack>
          </CardShell>
        </Grid>

        {/* 중단: 4개 카드 */}
        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <CardShell title="취소 · 반품 · 교환" icon={<CancelOutlinedIcon fontSize="small" />}>
            <Stack spacing={1}>
              {cancelStats.map((it) => (
                <Stack
                  key={it.label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{it.label}</Typography>
                  <Typography
                    sx={{ fontWeight: 900, color: it.value ? "error.main" : "text.primary" }}
                  >
                    {it.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardShell>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <CardShell title="판매 지연" icon={<LocalShippingOutlinedIcon fontSize="small" />}>
            <Stack spacing={1}>
              {delayStats.map((it) => (
                <Stack
                  key={it.label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{it.label}</Typography>
                  <Typography
                    sx={{ fontWeight: 900, color: it.value ? "error.main" : "text.primary" }}
                  >
                    {it.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardShell>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <CardShell title="상품 관리" icon={<Inventory2OutlinedIcon fontSize="small" />}>
            <Stack spacing={1}>
              {productStats.map((it) => (
                <Stack
                  key={it.label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{it.label}</Typography>
                  <Typography
                    sx={{ fontWeight: 900, color: it.value ? "primary.main" : "text.primary" }}
                  >
                    {it.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardShell>
        </Grid>

        <Grid size={{ xs: 12, md: 6, lg: 3 }}>
          <CardShell title="리뷰 현황" icon={<RateReviewOutlinedIcon fontSize="small" />}>
            <Stack spacing={1}>
              {reviewStats.map((it) => (
                <Stack
                  key={it.label}
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{it.label}</Typography>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: it.accent ? "primary.main" : it.value ? "error.main" : "text.primary",
                    }}
                  >
                    {it.value}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </CardShell>
        </Grid>

        {/* 하단: 고객 문의 현황 */}
        <Grid size={{ xs: 12 }}>
          <CardShell title="고객 문의 현황" icon={<PersonOutlineIcon fontSize="small" />}>
            {/* 탭 느낌 버튼들 */}
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
              {inquiryTabs.map((t) => (
                <Button
                  key={t.label}
                  variant={t.accent ? "contained" : "outlined"}
                  size="small"
                  sx={{ borderRadius: 999 }}
                >
                  {t.label} {typeof t.value === "number" ? t.value : ""}
                </Button>
              ))}
            </Stack>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 6 }}>
                <SimpleList items={inquiryList} />
                <Button variant="outlined" fullWidth sx={{ mt: 1.5, borderRadius: 1.5, py: 1.1 }}>
                  자세히 확인하기
                </Button>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <PlaceholderChart />
                  <MiniStatsRow
                    items={[
                      { label: "상담 수", value: 40 },
                      { label: "봇 응대", value: 0 },
                      { label: "상담 응답률", value: "59.5%" },
                      { label: "가장 많은 상담 분야", value: "상품정보" },
                    ]}
                  />
                  <Button variant="outlined" fullWidth sx={{ borderRadius: 1.5, py: 1.1 }}>
                    자세히 확인하기
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </CardShell>
        </Grid>

        {/* 맨 아래: 판매 성과 */}
        <Grid size={{ xs: 12 }}>
          <CardShell title="판매 성과" icon={<ShoppingBagOutlinedIcon fontSize="small" />}>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
              <Button variant="contained" size="small" sx={{ borderRadius: 999 }}>
                일간
              </Button>
              <Button variant="outlined" size="small" sx={{ borderRadius: 999 }}>
                주간
              </Button>
              <Button variant="outlined" size="small" sx={{ borderRadius: 999 }}>
                월간
              </Button>

              <Typography sx={{ ml: "auto", fontSize: 12, color: "text.secondary" }}>
                01.01 기준
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 3 }}>
                <MiniStatsRow items={[{ label: "방문 수", value: "8,636건", accent: true }]} />
              </Grid>
              <Grid size={{ xs: 12, md: 3 }}>
                <MiniStatsRow items={[{ label: "상품 주문건수", value: "735건", accent: true }]} />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <MiniStatsRow items={[{ label: "구매전환율", value: "8.5%", accent: true }]} />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <MiniStatsRow
                  items={[{ label: "상품주문단가", value: "38,922원", accent: true }]}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 2 }}>
                <MiniStatsRow
                  items={[{ label: "결제금액", value: "28,607,580원", accent: true }]}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <Box sx={{ mt: 2 }}>
                  <PlaceholderChart />
                </Box>
              </Grid>
            </Grid>
          </CardShell>
        </Grid>
      </Grid>
    </Box>
  );
}
